<template>
  <div class="stat-box">
    <template v-if="loading">
      <v-skeleton width="55%" height="16px" />
      <v-skeleton width="75%" height="32px" />
      <v-skeleton width="45%" height="16px" />
    </template>
    <template v-else>
      <span class="stat-box__label">{{ label }}</span>
      <span class="stat-box__value">
        <template v-if="format === 'currency'">{{ value | price }}</template>
        <template v-else-if="format === 'percent'">{{ value }}%</template>
        <template v-else>{{ value | formatNumber }}</template>
      </span>
      <span
        v-if="hasComparison"
        class="stat-box__trend"
        :class="`stat-box__trend--${tone}`"
      >
        <font-awesome-icon v-if="trend !== 'flat'" :icon="trendIcon" />
        <span>{{ changeText }} vs {{ comparisonLabel }}</span>
      </span>
    </template>
  </div>
</template>

<script>
import VSkeleton from '@/components/v3/elements/VSkeleton';

export default {
  name: 'VDashboardStatBox',
  components: { VSkeleton },
  props: {
    label: {
      type: String,
      default: '',
    },
    value: {
      type: Number,
      default: 0,
    },
    previousValue: {
      type: Number,
      default: null,
    },
    format: {
      type: String,
      default: 'number',
      validator: value => ['currency', 'number', 'percent'].includes(value),
    },
    comparisonLabel: {
      type: String,
      default: '',
    },
    invertTrend: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    hasComparison() {
      return this.previousValue !== null && this.previousValue !== undefined;
    },
    change() {
      if (!this.hasComparison || this.previousValue === 0) return 0;
      return ((this.value - this.previousValue) / Math.abs(this.previousValue)) * 100;
    },
    trend() {
      if (this.change > 0) return 'up';
      if (this.change < 0) return 'down';
      return 'flat';
    },
    tone() {
      if (this.trend === 'flat') return 'flat';
      const improved = this.invertTrend ? this.trend === 'down' : this.trend === 'up';
      return improved ? 'good' : 'bad';
    },
    trendIcon() {
      return this.trend === 'down' ? 'angle-down' : 'angle-up';
    },
    changeText() {
      return `${Math.abs(this.change).toFixed(1)}%`;
    },
  },
};
</script>

<style scoped lang="scss">
.stat-box {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px;
  background-color: #fff;
  border: 1px solid #e9ebf2;
  border-radius: 6px;
  min-height: 116px;

  &__label {
    @include font-size(12px, 16px);
    color: $color-gray-6C;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }

  &__value {
    @include font-size(24px, 32px);
    font-weight: 600;
    color: $color-gray-25;
  }

  &__trend {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    @include font-size(12px, 16px);
    color: $color-gray-6C;

    &--good {
      color: $color-primary-green;
    }

    &--bad {
      color: $color-primary-red;
    }
  }
}
</style>
